import { createContext, FC, useContext, useEffect } from 'react'
import { useCountry } from '..'
import { Country } from '../../constants'
import { loadCldr, setCulture } from '@syncfusion/ej2-base'

import * as allDE from '@syncfusion/ej2-cldr-data/main/de/all.json'
import * as allDECH from '@syncfusion/ej2-cldr-data/main/de-CH/all.json'
import * as allENDE from '@syncfusion/ej2-cldr-data/main/en-DE/all.json'

import * as numberingSystems from '@syncfusion/ej2-cldr-data/supplemental/numberingSystems.json'
import * as weekData from '@syncfusion/ej2-cldr-data/supplemental/weekData.json'
import i18next from 'i18next'

loadCldr(numberingSystems, allDE, allDECH, allENDE, weekData)

type LocaleHourCycleKey = 'h12' | 'h23' | 'h11' | 'h24'
interface LocaleContext {
  hourCycle: LocaleHourCycleKey
  country?: Country
  locale: string
}

const context = createContext<LocaleContext>({} as LocaleContext)
const { Provider } = context

const allowedSyncfusionLocales = ['en', 'en-US', 'en-DE', 'de', 'de-CH']

const getSyncfusionLocale = (locale: string) => {
  if (allowedSyncfusionLocales.includes(locale)) return locale

  const language = new Intl.Locale(locale || 'en').language

  switch (language) {
    case 'en':
    case 'de':
      return language
    default:
      return 'en'
  }
}

const getHourCyccle = (locale: string) => {
  const language = new Intl.Locale(locale || 'en').language
  switch (language) {
    case 'de':
      return 'h24'
    default:
      return 'h12'
  }
}

const LocaleProvider: FC = ({ children }) => {
  const country = useCountry()

  useEffect(() => {
    const sfLocale = getSyncfusionLocale(i18next.language)
    setCulture(sfLocale)
  }, [])

  return (
    <Provider
      value={{
        country: country as Country,
        locale: i18next.language,
        hourCycle: getHourCyccle(i18next.language),
      }}
    >
      {children}
    </Provider>
  )
}

const useLocale = (): LocaleContext => useContext(context)

export { LocaleProvider, useLocale }
export type { LocaleHourCycleKey }
